import { useQuery } from '@tanstack/react-query';
import { EMPTY_ARRAY } from '../../Constants/constants';
import {
  useAcceptedProcesses,
  useEvaluatedProcesses,
  useReadyToSignProcessesOfTutor,
  useReadyToSignWorkReportsOfTutor,
  useWaitingForReviewProcesses,
} from './ReviewHooks';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import {
  GetInternshipProcessesByUserId,
  GetInternshipProcessesOfUniversitySupervisor,
  GetOrganizationRegistrations,
} from '../../helper/apiHelper';
import { useCurrentUser } from '../useCurrentUser';
import {
  useApplicationCount,
  useConventionsToBeSignedCount,
  useStudentCount,
} from './InternshipApplicationHooks';
import { useUserOrganizations } from '../useOrganizations';
import {
  usePendingConventions,
  useUnregisteredOrganizations,
  useUnsignedRegistrations,
} from './LegalDepartmentHooks';
import type { InternshipProcessWithNames } from '../../Types/interfaces';
import {
  Roles,
  StepStatus,
  type OrganizationRegistration,
} from '../../Types/interfaces';
import {
  useEvaluatedReports,
  useReadyToSignConventions,
} from './SupervisorHooks';
import { useInternshipProcessesByRole } from '../useInternshipProcesses';
import { useCurrentUserRoles, useHasRole } from '../useHasRole';
import { useReadyToSignConventionsOfProjectCoordinator } from './ProjectCoordinatorHooks';

interface TaskTypeCount {
  hasTask: boolean;
  taskCount: number;
}

export const useHasTutorTasks = (): TaskTypeCount => {
  const loggedInUser = useCurrentUser();
  const hasRole = useHasRole();

  const { data: processes = EMPTY_ARRAY } = useQuery({
    queryKey: [...QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID, loggedInUser?.id],
    queryFn: async () => {
      return await GetInternshipProcessesByUserId(
        loggedInUser?.id?.toString() ?? ''
      );
    },
    enabled: !!loggedInUser?.id && hasRole(Roles.Tutor),
  });

  const readyToSignConventions = useReadyToSignProcessesOfTutor(processes);
  const waitingForReviewProcesses = useWaitingForReviewProcesses(processes);
  const workReportsToSign = useReadyToSignWorkReportsOfTutor(processes);
  const acceptedProcesses = useAcceptedProcesses(processes);
  const { data: evaluatedProcesses = [] } = useEvaluatedProcesses(processes);
  return {
    hasTask:
      readyToSignConventions.length !== 0 ||
      waitingForReviewProcesses.length !== 0 ||
      workReportsToSign.length !== 0 ||
      acceptedProcesses.length !== 0 ||
      evaluatedProcesses.length !== 0,

    taskCount:
      readyToSignConventions.length +
      waitingForReviewProcesses.length +
      workReportsToSign.length +
      acceptedProcesses.length +
      evaluatedProcesses.length,
  };
};

export const useHasOrganizationRepresentativeTasks = (): TaskTypeCount => {
  const loggedInUser = useCurrentUser();
  const { data: userOrganizations = EMPTY_ARRAY } = useUserOrganizations(
    loggedInUser?.id
  );

  const { data: applicationCount = 0 } = useApplicationCount(userOrganizations);
  const { data: studentCount = 0 } = useStudentCount(userOrganizations);
  const { data: conventionsToBeSignedCount = 0 } =
    useConventionsToBeSignedCount(userOrganizations);

  return {
    hasTask:
      applicationCount !== 0 ||
      studentCount !== 0 ||
      conventionsToBeSignedCount !== 0,
    taskCount: applicationCount + studentCount + conventionsToBeSignedCount,
  };
};

export const useHasLegalDepartmentMemberConventionTasks = (): TaskTypeCount => {
  const { data: pendingConventions = [] } = usePendingConventions();

  return {
    hasTask: pendingConventions.length !== 0,
    taskCount: pendingConventions.length,
  };
};

export const useHasLegalDepartmentMemberOrganizationTasks =
  (): TaskTypeCount => {
    const currentUserRoles = useCurrentUserRoles();
    const { data: organizationRegistrations = [] } = useQuery<
      Array<OrganizationRegistration>
    >({
      queryKey: [...QUERY_KEYS.ORGANIZATION_REGISTRATIONS],
      queryFn: () => GetOrganizationRegistrations(),
      enabled: currentUserRoles.some(
        (role) =>
          role === Roles.Administrator ||
          role === Roles.DepartmentRepresentative ||
          role === Roles.Rector
      ),
    });

    const unregisteredOrganizations = useUnregisteredOrganizations(
      organizationRegistrations,
      'SigningOrganizationRegistrationDocumentByLegalDepartment'
    );
    const unsignedRegistrations = useUnsignedRegistrations(
      organizationRegistrations,
      'SigningOrganizationRegistrationDocumentByLegalDepartment'
    );

    return {
      hasTask:
        unregisteredOrganizations.length !== 0 ||
        unsignedRegistrations.length !== 0,
      taskCount:
        unregisteredOrganizations.length + unsignedRegistrations.length,
    };
  };

export const useHasRectorOrganizationTasks = (): TaskTypeCount => {
  const currentUserRoles = useCurrentUserRoles();

  const { data: organizationRegistrations = [] } = useQuery<
    Array<OrganizationRegistration>
  >({
    queryKey: [...QUERY_KEYS.ORGANIZATION_REGISTRATIONS],
    queryFn: () => GetOrganizationRegistrations(),
    enabled: currentUserRoles.some(
      (role) =>
        role === Roles.Administrator ||
        role === Roles.DepartmentRepresentative ||
        role === Roles.Rector
    ),
  });

  const unsignedRegistrations = useUnsignedRegistrations(
    organizationRegistrations,
    'SigningCollaborationDocumentByRector'
  );

  return {
    hasTask: unsignedRegistrations.length !== 0,
    taskCount: unsignedRegistrations.length,
  };
};

export const useHasRectorInternshipTasks = (): TaskTypeCount => {
  const EMPTY_INTERNSHIP_PROCESSES_ARRAY: InternshipProcessWithNames[] = [];
  const loggedInUser = useCurrentUser();
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const userOrganizationIds = userOrganizations?.map((org) => org.id) ?? [];

  const { data: internshipProcesses = EMPTY_INTERNSHIP_PROCESSES_ARRAY } =
    useInternshipProcessesByRole(
      loggedInUser.id,
      Roles.Rector,
      userOrganizationIds
    );

  const readyToSigns = internshipProcesses.filter(
    (process) =>
      process.processSteps.find(
        (step) =>
          step.stepName === 'SigningInternshipConventionByRector' &&
          !step.disabled
      )?.stepStatus === StepStatus.initialized
  );

  return { hasTask: readyToSigns.length !== 0, taskCount: readyToSigns.length };
};

export const useHasProjectCoordinatorTasks = (): TaskTypeCount => {
  const EMPTY_INTERNSHIP_PROCESSES_ARRAY: InternshipProcessWithNames[] = [];
  const loggedInUser = useCurrentUser();
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const userOrganizationIds = userOrganizations?.map((org) => org.id) ?? [];

  const { data: internshipProcesses = EMPTY_INTERNSHIP_PROCESSES_ARRAY } =
    useInternshipProcessesByRole(
      loggedInUser.id,
      Roles.UniversityProjectCoordinator,
      userOrganizationIds
    );

  const readyToSign =
    useReadyToSignConventionsOfProjectCoordinator(internshipProcesses);

  return { hasTask: readyToSign.length !== 0, taskCount: readyToSign.length };
};

export const useHasSupervisorTasks = (): TaskTypeCount => {
  const loggedInUser = useCurrentUser();
  const hasRole = useHasRole();
  const isAuthorized =
    hasRole(Roles.Administrator) ||
    hasRole(Roles.CadruDidactic) ||
    hasRole(Roles.DepartmentRepresentative) ||
    hasRole(Roles.OrganizationRepresentative);
  const { data: internshipProcesses = [] } = useQuery<
    Array<InternshipProcessWithNames>
  >({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR,
      loggedInUser.id,
    ],
    queryFn: async () => {
      return await GetInternshipProcessesOfUniversitySupervisor(
        loggedInUser.id
      );
    },
    enabled: !!loggedInUser.id && isAuthorized,
  });
  const readyToSignConventions = useReadyToSignConventions(internshipProcesses);
  const evaluatedReports = useEvaluatedReports(internshipProcesses);

  return {
    hasTask:
      readyToSignConventions.length !== 0 || evaluatedReports.length !== 0,
    taskCount: readyToSignConventions.length + evaluatedReports.length,
  };
};
